/* BP-015 — Marketing Intelligence tab (workspace-level).
   Campaigns list + create + Executive Dashboard summary. */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, TrendingUp, DollarSign, Sparkles, Users, Target, RefreshCw } from "lucide-react";

const CHANNELS = [
  "facebook_ads", "google_ads", "tiktok", "youtube",
  "direct_mail", "cold_calling", "sms", "email",
  "driving_for_dollars", "bandit_signs", "referral",
  "organic_website", "custom",
];

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

export default function MarketingTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", channel: "facebook_ads", budget: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        api.get("/v3/marketing/campaigns"),
        api.get("/v3/marketing/dashboard/executive?model=linear"),
      ]);
      setCampaigns(c.data.items || []);
      setDashboard(d.data || null);
      try {
        const co = await api.get("/v3/marketing/coach/latest");
        setCoaching(co.data);
      } catch { setCoaching(null); }
    } catch { toast.error("Failed to load marketing data"); }
    finally { setLoading(false); }
  }, []);

  const generateCoach = useCallback(async (force = false) => {
    setCoachLoading(true);
    try {
      const { data } = await api.post(
        "/v3/marketing/coach/generate", { force });
      setCoaching(data);
      toast.success(data.cached ? "Loaded cached coaching" : "AI marketing coaching generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coaching failed");
    } finally { setCoachLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async () => {
    if (!form.name.trim()) { toast.error("Campaign name required"); return; }
    setCreating(true);
    try {
      await api.post("/v3/marketing/campaigns", {
        name: form.name, channel: form.channel,
        budget: form.budget || undefined,
      });
      toast.success("Campaign created");
      setForm({ name: "", channel: "facebook_ads", budget: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  }, [form, load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="mkt-loading">
      <Loader2 size={14} className="animate-spin"/> Loading marketing intelligence…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-marketing">
      {/* Executive dashboard */}
      <div className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="mkt-dashboard">
        <div className="text-[10px] uppercase tracking-wider text-amber-800/80 mb-2 flex items-center gap-1">
          <TrendingUp size={12}/> Executive Marketing Dashboard · Linear Attribution
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric l="Total Revenue" v={`$${fmt(dashboard?.total_revenue)}`}/>
          <Metric l="Total Cost"    v={`$${fmt(dashboard?.total_cost)}`}/>
          <Metric l="ROI"           v={`${dashboard?.roi_pct ?? 0}%`}/>
          <Metric l="ROAS"          v={`${dashboard?.roas ?? 0}x`}/>
        </div>
        {/* Phase 2 KPI tiles */}
        {dashboard?.kpis && Object.keys(dashboard.kpis).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="mkt-kpis">
            <KPI l="Cost / Lead" v={`$${fmt(dashboard.kpis.cost_per_lead)}`}/>
            <KPI l="Cost / Appt" v={`$${fmt(dashboard.kpis.cost_per_appointment)}`}/>
            <KPI l="Cost / Contract" v={`$${fmt(dashboard.kpis.cost_per_contract)}`}/>
            <KPI l="Cost / Assignment" v={`$${fmt(dashboard.kpis.cost_per_assignment)}`}/>
            <KPI l="Revenue / Lead" v={`$${fmt(dashboard.kpis.revenue_per_lead)}`}/>
            <KPI l="Avg Deal Value" v={`$${fmt(dashboard.kpis.avg_deal_value)}`}/>
            <KPI l="Lead → Close" v={`${dashboard.kpis.conversion_lead_to_close}%`}/>
          </div>)}
        {/* Phase 2 funnel */}
        {dashboard?.funnel && dashboard.funnel.leads > 0 && (
          <div className="mt-4" data-testid="mkt-funnel">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
              <Users size={10}/> Conversion Funnel
            </div>
            {[
              { key: "leads", label: "Leads Captured" },
              { key: "appointments", label: "Appointments Set" },
              { key: "contracts", label: "Contracts Signed" },
              { key: "closed", label: "Deals Closed" },
            ].map(({ key, label }) => {
              const v = dashboard.funnel[key] || 0;
              const pct = Math.max(2, Math.min(100, (v / dashboard.funnel.leads) * 100));
              return (
                <div key={key} className="grid grid-cols-12 gap-2 items-center py-0.5"
                      data-testid={`mkt-funnel-${key}`}>
                  <div className="col-span-4 text-xs text-slate-700">{label}</div>
                  <div className="col-span-6">
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                  <div className="col-span-2 text-right text-sm font-semibold tabular-nums">{v}</div>
                </div>);
            })}
          </div>)}
        {/* Channel breakdown */}
        {dashboard?.channel_breakdown && Object.keys(dashboard.channel_breakdown).length > 0 && (
          <div className="mt-4" data-testid="mkt-channels">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Channel Breakdown</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
              {Object.entries(dashboard.channel_breakdown).map(([ch, cb]) => (
                <div key={ch} className="flex justify-between items-center py-1 px-2 rounded bg-white/60"
                      data-testid={`mkt-ch-${ch}`}>
                  <span className="text-slate-800 capitalize">{ch.replace(/_/g, " ")} <span className="text-slate-400">· {cb.campaigns} camp · {cb.leads} leads</span></span>
                  <span className={`tabular-nums font-semibold ${cb.roi_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {cb.roi_pct}%
                  </span>
                </div>))}
            </div>
          </div>)}
        {(dashboard?.top_campaigns || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Top campaigns by ROI</div>
            {dashboard.top_campaigns.map((c) => (
              <div key={c.id} className="flex justify-between items-center py-1 text-xs">
                <span className="text-slate-900">{c.name} <span className="text-slate-400">· {c.channel}</span></span>
                <span className="tabular-nums font-semibold text-emerald-700">
                  {c.roi_pct}% · ${fmt(c.revenue)} rev
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Marketing Coach */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mkt-coach">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <Sparkles size={12} className="text-purple-600"/> AI Marketing Coach
            {coaching && (
              <span className="text-slate-500">
                · {coaching.model_name} · confidence {(coaching.confidence * 100).toFixed(0)}%
                {coaching.cached && <span className="ml-1 rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 uppercase text-[9px] font-bold">cached</span>}
              </span>)}
          </div>
          <div className="flex gap-2">
            {coaching && (
              <button onClick={() => generateCoach(true)} disabled={coachLoading}
                      data-testid="mkt-coach-regen"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-50">
                <RefreshCw size={12}/> Regenerate
              </button>)}
            <button onClick={() => generateCoach(false)} disabled={coachLoading}
                    data-testid="mkt-coach-generate"
                    className="inline-flex items-center gap-1 rounded-md bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-purple-700 disabled:opacity-50">
              {coachLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {coaching ? "Refresh" : "Generate Coaching"}
            </button>
          </div>
        </div>
        {!coaching && !coachLoading && (
          <p className="text-sm text-slate-600">
            No AI marketing coaching yet. Click <b>Generate Coaching</b> for an interpretive analysis of your channels + funnel + campaign ROI.
            <br/><span className="text-[11px] text-slate-500">Powered by Claude Sonnet 4.6. Advisory only.</span>
          </p>)}
        {coachLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 size={14} className="animate-spin"/> Generating marketing coaching…
          </div>)}
        {coaching && (
          <div className="space-y-3" data-testid="mkt-coach-output">
            <div className="text-base font-semibold text-slate-900">{coaching.headline}</div>
            {["strengths", "opportunities", "recommended_actions"].map((k) => {
              const items = coaching[k] || [];
              if (!items.length) return null;
              const tone = { strengths: ["emerald", "Strengths"],
                              opportunities: ["amber", "Opportunities"],
                              recommended_actions: ["sky", "Recommended Actions"] }[k];
              const dot = { emerald: "bg-emerald-500", amber: "bg-amber-500", sky: "bg-sky-500" }[tone[0]];
              const text = { emerald: "text-emerald-700", amber: "text-amber-700", sky: "text-sky-700" }[tone[0]];
              return (
                <div key={k} data-testid={`mkt-coach-${k}`}>
                  <div className={`text-[10px] uppercase tracking-wider font-semibold ${text} mb-1`}>{tone[1]}</div>
                  <ul className="space-y-1">
                    {items.map((it, i) => {
                      const txt = typeof it === "string" ? it : it?.text || "";
                      const paths = typeof it === "string" ? [] : it?.evidence_paths || [];
                      return (
                        <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                          <span className={`mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full ${dot}`}/>
                          <div>
                            <div>{txt}</div>
                            {paths.length > 0 && (
                              <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] text-slate-500">
                                {paths.map((p, j) => (<code key={j} className="rounded bg-slate-100 px-1 py-0.5">{p}</code>))}
                              </div>)}
                          </div>
                        </li>);
                    })}
                  </ul>
                </div>);
            })}
            <div className="text-[10px] text-slate-500 italic pt-1">{coaching.disclaimer}</div>
          </div>)}
      </div>

      {/* Create campaign */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mkt-create">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1">
          <Plus size={12}/> New Campaign
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Campaign name" data-testid="mkt-new-name" className={INP}/>
          <select value={form.channel} onChange={(e) => setForm((s) => ({ ...s, channel: e.target.value }))}
                    data-testid="mkt-new-channel" className={INP}>
            {CHANNELS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <input value={form.budget} onChange={(e) => setForm((s) => ({ ...s, budget: e.target.value }))}
                  placeholder="Budget $" type="number" data-testid="mkt-new-budget" className={INP}/>
          <button onClick={create} disabled={creating} data-testid="mkt-create-btn"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {creating ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} Create
          </button>
        </div>
      </div>

      {/* Campaigns list */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mkt-campaigns">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
          <DollarSign size={12}/> Campaigns ({campaigns.length})
        </div>
        {campaigns.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No campaigns yet. Create one above to start tracking spend and attribution.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left py-1">Name</th>
                <th className="text-left py-1">Channel</th>
                <th className="text-right py-1">Budget</th>
                <th className="text-right py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} data-testid={`mkt-row-${c.id.slice(0, 8)}`}
                     className="border-t border-slate-100">
                  <td className="py-1.5 text-slate-900 font-medium">{c.name}</td>
                  <td className="py-1.5 text-slate-600">{c.channel?.replace("_", " ")}</td>
                  <td className="py-1.5 text-right tabular-nums">{c.budget ? `$${fmt(c.budget)}` : "—"}</td>
                  <td className="py-1.5 text-right">
                    <span className={`inline-block text-[9px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ${
                      c.status === "active"    ? "bg-emerald-100 text-emerald-800"
                      : c.status === "paused"  ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"}`}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const Metric = ({ l, v }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
    <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{v}</div>
  </div>
);
const KPI = ({ l, v }) => (
  <div className="rounded-lg border border-slate-200 bg-white/70 p-2">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
    <div className="text-sm font-bold text-slate-900 tabular-nums">{v}</div>
  </div>
);
function fmt(v) { if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toLocaleString(undefined,{maximumFractionDigits:0}); }
